import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { Cabinet } from 'src/app/utils/interfaces/new/cabinet.interface';
import { CreateCabinet } from 'src/app/utils/interfaces/new/create-cabinet.interface';
import { Destinataire } from 'src/app/utils/interfaces/new/destinataire.interface';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { Profil } from 'src/app/utils/interfaces/new/profil.interface';
import { environment } from 'src/environments/environment';
import { FiltresService } from '../filtres/filtres.service';
import { SnackbarService } from '../snackbar/snackbar.service';

@Injectable({
  providedIn: 'root'
})
export class CabinetService {

  private isMock = environment.mock;

  private uriCabinet = `${environment.basePath}/hypermed/cabinets`;

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders()
  };

  // Fonction pratique
  // Permet de transformer la fonction en fonction asynchrone facilement (pour le mock)
  private _wait = (ms) => new Promise(res => setTimeout(res, ms));

  constructor(
    private _http: HttpClient,
    private _filtresService: FiltresService,
    private _snack: SnackbarService
  ) { }

  public async create(cabinet: CreateCabinet): Promise<string> {
    try {
      const res = await this._http.post(this.uriCabinet, cabinet, this.httpOptions).toPromise();
      const id = res.body as string;
      return id;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async find(filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'nom'
  }): Promise<Paginated<Cabinet>> {
    try {
      this.httpOptions.headers = this._filtresService.setHeaders(filtre);
      const res = await this._http.get(this.uriCabinet, this.httpOptions).toPromise();
      const cabinets = res.body as Cabinet[];
      const paginated: Paginated<Cabinet> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: cabinets
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOne(id: string): Promise<Cabinet> {
    try {
      const res = await this._http.get(this.uriCabinet + `/${id}`, this.httpOptions).toPromise();
      const cabinet = res.body as Cabinet;
      return cabinet;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async delete(cabinet: Cabinet): Promise<void> {
    try {
      const res = await this._http.delete(this.uriCabinet + `/${cabinet.id}`, this.httpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async update(cabinet: Cabinet): Promise<void> {
    try {
      const res = await this._http.put(this.uriCabinet + `/${cabinet.id}`, cabinet, this.httpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findCabinetProfils(idCabinet: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'nom'
  }): Promise<Paginated<Profil>> {
    try {
      this.httpOptions.headers = this._filtresService.setHeaders(filtre);
      const res = await this._http.get(`${environment.basePath}/hypermed/cabinets/${idCabinet}/profils`, this.httpOptions).toPromise();
      const profils = res.body as Profil[];
      const paginated: Paginated<Profil> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: profils
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  /**
   * On utilise le destinataire qui renvoie sensiblement les même information (TODO si besoin de plus de détail profil)
   * @returns
   */
  public async findCurrentCabinetProfils() {
    return this.findCurrentCabinetDestinataire();
  }

  public async findCurrentCabinetDestinataire() {
    try {
      const res = await this._http.get(this.uriCabinet + `/current/destinataires`, this.httpOptions).toPromise();
      const destinataires = res.body as Destinataire[];
      return destinataires;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findCurrentCabinetDestinataireForLogs() {
    try {
      const res = await this._http.get(this.uriCabinet + `/current/destinataires`, this.httpOptions).toPromise();
      const destinataires = res.body as Destinataire[];
      return destinataires;
    } catch (error) {
      return undefined;
    }
  }

  public async isCabinetMsp(): Promise<boolean | PromiseLike<boolean>> {
    try {
      const res = await this._http.get(this.uriCabinet + `/current/ismsp`, this.httpOptions).toPromise();
      const isMsp = res.body as boolean;
      return isMsp;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async deployCabinet() {
    try {
      const res = await this._http.get(this.uriCabinet + `/current/destinataires`, this.httpOptions).toPromise();
      const destinataires = res.body as Destinataire[];
      return destinataires;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }
}
